import React, { useEffect, useRef, useState } from "react";
import { loadGoogleMapsAPI } from "../utils/googleMapsLoader";

interface GoogleMapProps {
  address?: string;
  center?: { lat: number; lng: number };
  height?: string;
  apiKey: string;
  onLocationSelect?: (location: {
    lat: number;
    lng: number;
    address: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  }) => void;
}

const GoogleMap: React.FC<GoogleMapProps> = ({
  address,
  center,
  height = "300px",
  apiKey,
  onLocationSelect,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const isInitializedRef = useRef(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string>("");

  // Helper to extract address components from geocoder result
  const extractAddressComponents = (
    result: google.maps.GeocoderResult,
  ): {
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  } => {
    let city: string | undefined;
    let state: string | undefined;
    let zipCode: string | undefined;
    let country: string | undefined;

    result.address_components.forEach((component) => {
      const types = component.types;

      if (types.includes("locality")) {
        city = component.long_name;
      } else if (types.includes("sublocality") && !city) {
        city = component.long_name;
      } else if (types.includes("administrative_area_level_1")) {
        state = component.short_name;
      } else if (types.includes("postal_code")) {
        zipCode = component.long_name;
      } else if (types.includes("country")) {
        country = component.short_name;
      }
    });

    return { city, state, zipCode, country };
  };

  // Load Google Maps
  useEffect(() => {
    loadGoogleMapsAPI(apiKey)
      .then(() => {
        setIsLoaded(true);
      })
      .catch((e: Error) => {
        setError("Failed to load Google Maps");
        console.error("Google Maps loading error:", e);
      });
  }, [apiKey]);

  // Initialize map after Google Maps is loaded
  useEffect(() => {
    if (!isLoaded || isInitializedRef.current) return;
    initializeMap();
    isInitializedRef.current = true;
  }, [isLoaded]);

  useEffect(() => {
    if (!isLoaded || !address || !address.trim() || center) return;
    geocodeAddress(address);
  }, [address, isLoaded, center]);

  // Center map when explicit coordinates are provided (e.g., user selected from autocomplete)
  useEffect(() => {
    if (!isLoaded || !center) return;

    // Small delay to ensure map is fully initialized
    const timeoutId = setTimeout(() => {
      if (!mapInstanceRef.current) {
        return;
      }

      mapInstanceRef.current.setCenter(center);
      updateMarker(center.lat, center.lng);
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [center, isLoaded]);

  const initializeMap = () => {
    if (!mapRef.current) {
      return;
    }
    const defaultCenter = { lat: 40.7128, lng: -74.006 }; // New York City

    // Use lower zoom when no address/center is provided to show more context
    const initialZoom = address || center ? 15 : 12;

    mapInstanceRef.current = new google.maps.Map(mapRef.current, {
      zoom: initialZoom,
      center: defaultCenter,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    geocoderRef.current = new google.maps.Geocoder();

    // Add click listener for manual location selection
    mapInstanceRef.current.addListener(
      "click",
      (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();

          // Reverse geocode to get address
          geocoderRef.current?.geocode(
            { location: { lat, lng } },
            (
              results: google.maps.GeocoderResult[] | null,
              status: google.maps.GeocoderStatus,
            ) => {
              if (status === "OK" && results && results[0]) {
                const address = results[0].formatted_address;
                const components = extractAddressComponents(results[0]);
                updateMarker(lat, lng);
                onLocationSelect?.({
                  lat,
                  lng,
                  address,
                  ...components,
                });
              }
            },
          );
        }
      },
    );
  };

  const geocodeAddress = (address: string) => {
    if (!geocoderRef.current || !mapInstanceRef.current) return;

    geocoderRef.current.geocode(
      { address },
      (
        results: google.maps.GeocoderResult[] | null,
        status: google.maps.GeocoderStatus,
      ) => {
        if (status === "OK" && results && results[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();

          mapInstanceRef.current?.setCenter({ lat, lng });
          updateMarker(lat, lng);
          // Intentionally do NOT call onLocationSelect here to avoid
          // overwriting the input while the user is typing.
        } else {
          console.error("Geocoding failed:", status);
          setError(`Could not find location: ${address}`);
        }
      },
    );
  };

  const updateMarker = (lat: number, lng: number) => {
    if (!mapInstanceRef.current) return;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Add new marker
    markerRef.current = new google.maps.Marker({
      position: { lat, lng },
      map: mapInstanceRef.current,
      title: "Pickup Location",
      draggable: true,
    });

    // Handle marker drag
    markerRef.current.addListener("dragend", () => {
      if (markerRef.current) {
        const position = markerRef.current.getPosition();
        if (position) {
          const lat = position.lat();
          const lng = position.lng();

          // Reverse geocode to get address
          geocoderRef.current?.geocode(
            { location: { lat, lng } },
            (
              results: google.maps.GeocoderResult[] | null,
              status: google.maps.GeocoderStatus,
            ) => {
              if (status === "OK" && results && results[0]) {
                const address = results[0].formatted_address;
                const components = extractAddressComponents(results[0]);
                onLocationSelect?.({
                  lat,
                  lng,
                  address,
                  ...components,
                });
              }
            },
          );
        }
      }
    });
  };

  if (error) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center bg-muted rounded-md border"
      >
        <div className="text-center p-4">
          <p className="text-muted-foreground text-sm">{error}</p>
          {error.includes("API key") && (
            <p className="text-xs text-muted-foreground mt-2">
              Get your API key from Google Cloud Console
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center bg-muted rounded-md border"
      >
        <div className="text-center p-4">
          <p className="text-muted-foreground text-sm">Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={mapRef} style={{ height }} className="w-full rounded-md border" />
  );
};

export default GoogleMap;
